import * as React from 'react';
import Popup from '../Popup';
import { ErrorMessageType } from 'src/utils/constValues';

export type Props = {
    showPopup: boolean,
    title?: string,
    confirmMessage?: string;
    customTextYesButton?: string;
    loading: boolean;
    loadingMessage?: string;
    shouldHideNoButton?: boolean;
    // Custom for step 01, 02, 04
    inStepOne?: boolean;
    stepOneType?: string; // EXIST_USER_FACILITY, TERMINATED_NPI, or empty
    stepOneMessage?: string;
    inStepTwo?: boolean;
    inStepFour?: boolean;
    stepFourType?: string; // ERROR_FROM_STRIPE, ERROR_FROM_INTERNAL_SYSTEM
    onCancel: () => void;
    executeFunction: () => void;
};

type State = {
    // user to check auto focus at the first time show popup
    firstLoading: boolean,
    isExecuted: boolean,
};

class PopupConfirm extends React.Component<Props, State> {
    private comPopup: Popup;
    constructor(props: Props) {

        super(props);
        this.state = this.getInitialState();
    }

    getInitialState = (): State => {
        return {
            firstLoading: true,
            isExecuted: false,
        };
    }

    componentDidMount() {
        if (this.props.showPopup) {
            this.comPopup.open();
            this.setState({ isExecuted: false });
        }
    }

    componentDidUpdate() {
        if (this.props.showPopup && this.state.firstLoading) {
            this.setState({ firstLoading: false });
        }
    }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.showPopup !== this.props.showPopup) {
            if (nextProps.showPopup) {
                this.comPopup.open();
                this.setState({ isExecuted: false });
            } else {
                this.comPopup.close();
                this.setState((prevState: State) => {
                    return {
                        ...prevState,
                        firstLoading: true
                    };
                });
            }
        }
    }

    onYesButtonClick = () => {
        if (!this.props.loading) {
            this.props.executeFunction();
            this.setState({ isExecuted: true });
        }
    }

    onEnterButtonPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (!this.props.loading) {
                this.props.executeFunction();
                this.setState({ isExecuted: true });
            }
        }
    }

    onClosePopup = () => {
        if (!this.props.loading) {
            this.props.onCancel();
        }
    }

    renderStepOneMessage = () => {
        const { inStepOne, stepOneType, stepOneMessage } = this.props;

        if (!inStepOne) {
            return null;
        }

        switch (stepOneType) {
            case ErrorMessageType.EXIST_USER_FACILITY:
                return (
                    <span className="col-auto col-form-label">
                        Dear provider,
                        <br /><br />
                        {stepOneMessage || 'You already had an account with us at this clinic.'}
                    </span>
                );
            
            case ErrorMessageType.TERMINATED_USER :
                return (
                    <span className="col-auto col-form-label">
                        Dear provider,
                        <br /><br />
                        Your registration has failed because your NPI (National Provider Identifier) is terminated.
                        <br /><br/>
                        To continue, please verify your NPI status and ensure that you have a valid and active NPI before restarting the registration process.
                        <br/><br/>
                        Please click OK to restart the registration process.
                    </span>
                );
            
            case ErrorMessageType.GENERAL_ERROR:
                return (
                    <span className="col-auto col-form-label">
                        Dear provider,
                        <br /><br />
                        We encountered an unexpected issue while verifying your information. The system was unable to check for existing users properly.
                        <br /><br />
                        If you receive this error message, please email us at <span style={{fontStyle: 'italic', fontWeight: 'bold' }}>support@acscripts.com</span>. Sorry for your inconvenience. We will contact you as soon as possible. Thank you.
                        <br /><br />
                        Please click OK to restart the registration process. 
                    </span>
                );

            default:
                return (
                    <span className="col-auto col-form-label">
                        Dear provider,
                        <br /><br />
                        You have failed your verification with Experian Company due to some errors with your submission of personal information. Please review and resubmit your correct personal information.
                        <br /><br />
                        Remember, you only have three attempts of submitting your personal information with Experian Company before they lock your name for 30 days.
                        <br /><br />
                        Please click OK to restart the registration process. 
                    </span>
                );
        }
    }

    renderStepFourMessage = () => {
        const { inStepFour, stepFourType } = this.props;

        if (!inStepFour) {
            return null;
        }

        switch (stepFourType) {
            case ErrorMessageType.ERROR_FROM_STRIPE:
                return (
                    <span className="col-auto col-form-label">
                        Dear provider,
                        <br /><br />
                        There was an error in your credit card payment information, please check your credit card payment information and try again, or contact us at <a href="mailto:support@acscripts.com" className="external-link">support@acscripts.com</a>.
                        <br /><br />
                        Thank you.
                    </span>
                );

            case ErrorMessageType.ERROR_FROM_INTERNAL_SYSTEM:
                return (
                    <span className="col-auto col-form-label">
                        Dear provider,
                        <br /><br />
                        We encountered an unexpected issue while verifying your information. The system was unable to submit payment properly.
                        <br /><br />
                        If you receive this error message, please email us at <span style={{ fontStyle: 'italic', fontWeight: 'bold' }}>support@acscripts.com</span>. Sorry for your inconvenience. We will contact you as soon as possible. Thank you.
                    </span>
                );

            default:
                return (
                    <span className="col-auto col-form-label">
                        Dear provider,
                        <br /><br />
                        We encountered an unexpected issue while verifying your information. The system was unable to submit payment properly.
                        <br /><br />
                        If you receive this error message, please email us at <span style={{ fontStyle: 'italic', fontWeight: 'bold' }}>support@acscripts.com</span>. Sorry for your inconvenience. We will contact you as soon as possible. Thank you.
                    </span>
                );
        }
    }

    render() {
        const { loading, title, confirmMessage, loadingMessage, shouldHideNoButton,
            customTextYesButton, inStepOne, inStepTwo, inStepFour } = this.props;
        let textYesButton = customTextYesButton ? customTextYesButton : ' Close';
        return (
            <Popup
                ref={com => this.comPopup = com!}
                title={title ?? ''}
                close={this.onClosePopup}
                popupHidden={this.onClosePopup}
                extendClass=""
                footer={
                    <>
                        {
                            shouldHideNoButton != undefined && shouldHideNoButton
                                ? null
                                : <button id="btnCancelConfirmPopup" type="button" disabled={loading} style={{ marginRight: '10px' }} className="btn btn-sm btn-light" onClick={this.onClosePopup}>
                                    <i className="fa fa-times-circle" /> No</button>
                        }

                        <button id="btnYesConfirmPopup" type="button" disabled={loading} className={`btn btn-sm ${shouldHideNoButton ? 'btn-light' : 'btn-primary'}`} onClick={this.onYesButtonClick}>
                            <i className="fa fa-sign-in" />{textYesButton}
                        </button>
                    </>
                }
            >
                {
                    loading ?
                        <div className="form-group row loading-center">
                            <label className="col-auto col-form-label">{loadingMessage}</label>
                            <span className="spinner-border text-info" style={{ marginLeft: '155px' }} role="status" aria-hidden="true" />
                        </div>
                        :
                        <div className="form-group row">
                            {
                                inStepOne && inStepOne ?
                                    this.renderStepOneMessage()
                                : inStepTwo && inStepTwo ?
                                    <span className="col-auto col-form-label">
                                        Dear provider,
                                        <br /><br />
                                        You have failed the questionnaire, please try to carefully answer the questionnaire again.
                                        <br /><br />
                                        Please note if after three times failing with Experian Company, they will lock your name and not allow you to submit again in 30 days.
                                    </span> 
                                : !!inStepFour ?
                                    this.renderStepFourMessage()
                                : confirmMessage ? <span className={'col-auto col-form-label'}>{confirmMessage}</span>
                                : <span className="col-auto col-form-label" style={{ color: '#333' }}>
                                    Thank you for submitting your registration application.
                                    <br /><br />
                                    We will send your application through the ID proofing credentialing process and the Surescripts credentialing process.
                                    <br /><br />
                                    We will send a confirmation email letting you know if everything is approved or if there is any problem with your application.
                                    <br /><br />
                                    And if everything is approved, we will notify you the final steps in getting set up.
                                </span>
                            }
                        </div>

                }
            </Popup>
        );
    }
}

export default PopupConfirm;


// WEBPACK FOOTER //
// ./src/components/shared/PopupConfirm/PopupConfirm.tsx